import React, { useState } from 'react';
import { uploadData } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import './FileUpload.css';

function TitleFileUpload({ onUploadComplete }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [company, setCompany] = useState('gri');
  const [loanGuid, setLoanGuid] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setUploadStatus('Please select a valid file (PDF, PNG, or JPG)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadStatus('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setUploadStatus('');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const generateFileName = () => {
    // Extract the filename without extension to use as document ID
    const fileName = selectedFile.name;
    const lastDotIndex = fileName.lastIndexOf('.');
    
    if (lastDotIndex === -1) {
      // No extension found, use the whole filename
      return fileName;
    }
    
    // Return the original filename (keeps the extension)
    return fileName;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('Please select a file first');
      return;
    }

    if (!loanGuid) {
      setUploadStatus('Please enter a loan GUID');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Uploading...');

    try {
      // Fetch AWS credentials from Cognito Identity Pool
      await fetchAuthSession({ forceRefresh: true });
      
      const fileName = generateFileName();
      // Use TITLE_REPORT prefix for title documents
      const s3Key = `TITLE_REPORT/${company}/${loanGuid}/${fileName}`;

      const result = await uploadData({
        path: s3Key,
        data: selectedFile,
        options: {
          contentType: selectedFile.type,
          accessLevel: 'guest',
          onProgress: ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              const progress = Math.round((transferredBytes / totalBytes) * 100);
              setUploadProgress(progress);
            }
          }
        }
      }).result;

      setUploadStatus('✓ Upload successful! Processing will begin automatically.');
      setUploadProgress(100);
      
      if (onUploadComplete) {
        onUploadComplete({
          fileName: s3Key,
          company,
          loanGuid,
          uploadTime: new Date().toISOString()
        });
      }

      setTimeout(() => {
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadStatus('');
        setLoanGuid('');
      }, 2000);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(`✗ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="file-upload-container">
      <div className="dashboard-header-section">
        <div className="dashboard-title">
          <i className="fas fa-file-contract"></i>
          <h1>Upload Title Document</h1>
        </div>
        <p className="dashboard-subtitle">Upload title report documents for automated processing</p>
      </div>
      
      <div className="card upload-card">

        <div className="upload-body-content">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="company"><i className="fas fa-building"></i> Company</label>
              <select
                id="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={uploading}
                className="form-control"
              >
                <option value="gri">GRI</option>
                <option value="gra">GRA</option>
                <option value="op">OP</option>
                <option value="kbhs">KBHS</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="loanGuid"><i className="fas fa-key"></i> Loan GUID *</label>
              <input
                type="text"
                id="loanGuid"
                value={loanGuid}
                onChange={(e) => setLoanGuid(e.target.value)}
                placeholder="Enter loan GUID"
                disabled={uploading}
                className="form-control"
              />
            </div>
          </div>

          <div 
            className={`drop-zone ${dragActive ? 'active' : ''} ${selectedFile ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="fileInput"
              onChange={handleFileSelect}
              accept=".pdf,.png,.jpg,.jpeg"
              disabled={uploading}
              style={{ display: 'none' }}
            />
            
            {!selectedFile ? (
              <label htmlFor="fileInput" className="drop-zone-label">
                <div className="drop-zone-icon">
                  <i className="fas fa-file-upload"></i>
                </div>
                <p className="drop-zone-text">
                  Drag and drop your file here, or click to browse
                </p>
                <p className="drop-zone-hint">
                  Supported formats: PDF, PNG, JPG (Max 10MB)
                </p>
              </label>
            ) : (
              <div className="selected-file">
                <div className="file-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="file-info">
                  <p className="file-name">{selectedFile.name}</p>
                  <p className="file-size">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                {!uploading && (
                  <button
                    className="remove-file-btn"
                    onClick={() => setSelectedFile(null)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            )}
          </div>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <span className="progress-text">{uploadProgress}%</span>
            </div>
          )}

          {uploadStatus && (
            <div className={`upload-status ${
              uploadStatus.includes('successful') ? 'success' : 
              uploadStatus.includes('failed') ? 'error' : 'info'
            }`}>
              {uploadStatus}
            </div>
          )}

          <button
            className="btn-primary upload-btn"
            onClick={handleUpload}
            disabled={!selectedFile || uploading || !loanGuid}
          >
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Uploading...
              </>
            ) : (
              <>
                <i className="fas fa-upload"></i> Upload Document
              </>
            )}
          </button>

          <div className="upload-info-card">
            <p className="info-text">
              Your title document will be automatically processed and analyzed for approval.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TitleFileUpload;
